import React from 'react';
import imageManifest from '../utils/imageManifest.json';

// Type for manifest entries
type ManifestEntry = { width: number; height: number; format: string };
const manifest = imageManifest as Record<string, ManifestEntry>;

/**
 * Get actual image dimensions from manifest
 * IMPORTANT: Use the -1200.webp variant dimensions (largest we serve via srcset)
 * NOT the source image dimensions, which may be much larger
 * Falls back to 3:2 (1200x800) if not found
 */
export function getImageDimensions(coverImage: string): { width: number; height: number } {
  // Convert source path to -1200.webp variant path
  // e.g., /blog_assets/image.jpg -> /blog_assets/image-1200.webp
  const lastDotIndex = coverImage.lastIndexOf('.');
  if (lastDotIndex !== -1) {
    const variant1200Path = coverImage.substring(0, lastDotIndex) + '-1200.webp';
    const variantEntry = manifest[variant1200Path];
    if (variantEntry) {
      return { width: variantEntry.width, height: variantEntry.height };
    }
  }

  // Fallback: try source image
  const entry = manifest[coverImage];
  if (entry) {
    return { width: entry.width, height: entry.height };
  }

  // Final fallback to 3:2 aspect ratio
  return { width: 1200, height: 800 };
}

/**
 * Get aspect ratio string for CSS (e.g., "3/2" or "1200/675")
 */
export function getImageAspectRatio(coverImage: string): string {
  const { width, height } = getImageDimensions(coverImage);
  return `${width}/${height}`;
}

interface BlogImageProps {
  /**
   * Cover image path from blog post frontmatter
   */
  coverImage: string;

  /**
   * Post title (used as alt text)
   */
  title: string;

  /**
   * CSS classes to apply
   */
  className?: string;

  /**
   * Loading strategy: 'lazy' or 'eager'
   */
  loading?: 'lazy' | 'eager';

  /**
   * Priority image (for hero/LCP images)
   */
  priority?: boolean;

  /**
   * Variant: 'card' | 'hero' | 'preview'
   */
  variant?: 'card' | 'hero' | 'preview';
}

/**
 * Generate responsive srcset for optimized image delivery
 * Generates srcset with WebP variants at 400, 800, 1200 widths
 */
function getResponsiveSrcset(coverImage: string): string {
  const lastDotIndex = coverImage.lastIndexOf('.');
  if (lastDotIndex === -1) return '';

  const basePath = coverImage.substring(0, lastDotIndex);

  // Generate WebP srcset - these files exist in /public/blog_assets/
  return `${basePath}-400.webp 400w, ${basePath}-800.webp 800w, ${basePath}-1200.webp 1200w`;
}

/**
 * Get the 400w WebP variant path for fast LCP loading
 */
function get400wPath(coverImage: string): string {
  const lastDotIndex = coverImage.lastIndexOf('.');
  if (lastDotIndex === -1) return coverImage;
  return coverImage.substring(0, lastDotIndex) + '-400.webp';
}

/**
 * BlogImage Component
 * Renders an optimized image with:
 * - Proper width/height from imageManifest to prevent CLS
 * - fetchPriority="high" for LCP images
 * - Responsive srcset with WebP for optimal delivery
 */
export const BlogImage: React.FC<BlogImageProps> = ({
  coverImage,
  title,
  className = '',
  loading = 'lazy',
  priority = false,
  variant = 'card',
}) => {
  const actualLoading = priority ? 'eager' : loading;

  // CLS FIX: Use actual image dimensions from manifest to prevent layout shift
  // Browser reserves exact space needed based on width/height attributes
  const { width, height } = getImageDimensions(coverImage);

  // LCP OPTIMIZATION: Hero images use 400w directly for fast LCP
  // No srcset, no swap - just the small image (~26KB)
  // Slightly blurry on desktop but fast LCP is the priority
  if (variant === 'hero') {
    const heroSrc = get400wPath(coverImage);
    return (
      <img
        src={heroSrc}
        alt={title}
        loading={actualLoading}
        fetchPriority={priority ? 'high' : undefined}
        width={width}
        height={height}
        className={className}
      />
    );
  }

  // For non-hero images: use srcset for responsive loading
  const srcset = getResponsiveSrcset(coverImage);
  // Preview cards are displayed at ~346px on desktop (2-3 column grid)
  // Card variant is displayed at ~700px (full article width)
  const sizes = variant === 'preview'
    ? '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 346px'
    : '(max-width: 768px) 100vw, 700px';

  // CLS FIX: width/height from manifest tells browser exact space to reserve
  // This prevents layout shift when srcset serves the actual image
  return (
    <img
      src={coverImage}
      alt={title}
      loading={actualLoading}
      fetchPriority={priority ? 'high' : undefined}
      width={width}
      height={height}
      srcSet={srcset || undefined}
      sizes={srcset ? sizes : undefined}
      className={className}
    />
  );
};

export default BlogImage;
